import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient, type B2BAllowedAuthMethods } from '@stytch/react/b2b';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useOrganizationBySlug } from '@/api/organization';
import EmailIcon from '@/assets/svg/envelope.svg?react';
import Google from '@/assets/svg/google.svg?react';
import LockIcon from '@/assets/svg/lock.svg?react';
import Microsoft from '@/assets/svg/microsoft.svg?react';
import { PageSpinner } from '@/components/loaders';
import { FullPage } from '@/components/page';
import { Link, Text } from '@/components/typography';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import {
  getTenantPreferredSignInMethod,
  setTenantPendingSignInMethod,
} from '@/helper/user-preferences';
import useDeferredLoading from '@/hooks/useDeferredLoading';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import AuthMethodButtons, { type AuthMethodConfig } from '../AuthMethodButtons';
import SignInPanel from '../SignInPanel';
import TermsOfUseLink from '../TermsOfUseLink';
import InstanceError from './InstanceError';
import TenantAuthenticate from './TenantAuthenticate';

const InstanceSignIn = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const stytch = useStytchB2BClient();
  const { pushNotification } = useNotification();
  const { organization, isLoading } = useOrganizationBySlug(orgSlug);
  const showLoading = useDeferredLoading(isLoading);

  // Get the stored preferred method and map 'sso' to the actual button ID
  const effectivePreferredMethod = useMemo(() => {
    if (!orgSlug) return null;
    const stored = getTenantPreferredSignInMethod(orgSlug);

    // If preferred is 'sso', map to the default SSO connection's button ID
    if (stored === 'sso' && organization) {
      const defaultConnectionId = organization.sso_default_connection_id;
      const connections = organization.sso_active_connections;

      if (defaultConnectionId) {
        return `sso-${defaultConnectionId}`;
      }
      // Fall back to first connection if no default is set
      if (connections && connections.length > 0) {
        return `sso-${connections[0].connection_id}`;
      }
    }

    return stored;
  }, [orgSlug, organization]);

  const redirectUrl = `${window.location.origin}${ROUTES.signIn.path}`;

  const handleOAuthClick = useCallback(
    async (provider: 'google' | 'microsoft') => {
      if (!organization || !orgSlug) return;

      setTenantPendingSignInMethod(orgSlug, provider);

      try {
        await stytch.oauth[provider].start({
          organization_id: organization.organization_id,
          login_redirect_url: redirectUrl,
        });
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
        });
      }
    },
    [organization, orgSlug, stytch, pushNotification, redirectUrl],
  );

  const handleSSOClick = useCallback(
    async (connectionId: string) => {
      if (!orgSlug) return;

      setTenantPendingSignInMethod(orgSlug, 'sso');

      try {
        await stytch.sso.start({
          connection_id: connectionId,
          login_redirect_url: redirectUrl,
        });
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signIn.path),
        });
      }
    },
    [orgSlug, stytch, pushNotification, redirectUrl],
  );

  const handleEmailClick = useCallback(() => {
    const emailPath = ROUTES.signInInstEmail.path.replace(':orgSlug', orgSlug ?? '');
    navigate(emailPath);
  }, [navigate, orgSlug]);

  const authMethods = useMemo((): AuthMethodConfig[] => {
    if (!organization) return [];

    const methods: AuthMethodConfig[] = [];
    const allowedMethods = organization.allowed_auth_methods || [];
    const authMethodsMode = organization.auth_methods;

    const isMethodAllowed = (method: B2BAllowedAuthMethods) => {
      // If auth_methods is ALL_ALLOWED, all methods are allowed
      if (authMethodsMode === 'ALL_ALLOWED') return true;
      // Otherwise, check the allowed_auth_methods list
      return allowedMethods.includes(method);
    };

    // SSO connections come first for tenant sign-in
    if (isMethodAllowed('sso') && organization.sso_active_connections) {
      for (const connection of organization.sso_active_connections) {
        methods.push({
          id: `sso-${connection.connection_id}`,
          label: t`Continue with ${connection.display_name}`,
          icon: <LockIcon />,
          onClick: () => handleSSOClick(connection.connection_id),
          rateLimit: true,
        });
      }
    }

    // OAuth methods
    if (isMethodAllowed('google_oauth')) {
      methods.push({
        id: 'google',
        label: t`Continue with Google`,
        icon: <Google />,
        onClick: () => handleOAuthClick('google'),
        rateLimit: true,
      });
    }

    if (isMethodAllowed('microsoft_oauth')) {
      methods.push({
        id: 'microsoft',
        label: t`Continue with Microsoft`,
        icon: <Microsoft />,
        onClick: () => handleOAuthClick('microsoft'),
        rateLimit: true,
      });
    }

    if (isMethodAllowed('magic_link')) {
      methods.push({
        id: 'email',
        label: t`Continue with email`,
        icon: <EmailIcon />,
        onClick: handleEmailClick,
      });
    }

    return methods;
  }, [organization, handleEmailClick, handleOAuthClick, handleSSOClick]);

  // Build method labels including dynamic SSO connection labels
  const methodLabels = useMemo(() => {
    const labels: Record<string, string> = {
      google: t`Google`,
      microsoft: t`Microsoft`,
      email: t`email`,
    };

    // Add labels for each SSO connection
    if (organization?.sso_active_connections) {
      for (const connection of organization.sso_active_connections) {
        labels[`sso-${connection.connection_id}`] = connection.display_name;
      }
    }

    return labels;
  }, [organization]);

  if (!orgSlug) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={<Trans>No instance specified.</Trans>}
      />
    );
  }

  if (isLoading && showLoading) {
    return <PageSpinner />;
  }

  if (isLoading) {
    return null;
  }

  if (!organization) {
    return (
      <InstanceError
        title={t`Instance Not Found`}
        message={
          <Trans>The instance "{orgSlug}" was not found or you don't have access to it.</Trans>
        }
      />
    );
  }

  if (authMethods.length === 0) {
    return (
      <InstanceError
        title={t`No Sign In Methods Available`}
        message={
          <Trans>
            This instance doesn't have any sign-in methods configured. Please contact your
            administrator.
          </Trans>
        }
      />
    );
  }

  return (
    <FullPage title={t`Sign In`}>
      <TenantAuthenticate orgSlug={orgSlug}>
        <SignInPanel
          title={t`Sign In to ${organization.organization_name}`}
          footer={
            <>
              <Text variant="caption1" color="grey">
                <Trans>
                  Don't want to sign in to {organization.organization_name}?{' '}
                  <Link variant="caption1" color="aqua" to={ROUTES.signIn.path}>
                    Try another instance
                  </Link>
                </Trans>
              </Text>
              <Text variant="caption1" color="grey">
                <Trans>
                  By continuing to sign in I agree to the <TermsOfUseLink />
                </Trans>
              </Text>
            </>
          }
          showGap
        >
          <AuthMethodButtons
            methods={authMethods}
            preferredMethodId={effectivePreferredMethod}
            methodLabels={methodLabels}
          />
        </SignInPanel>
      </TenantAuthenticate>
    </FullPage>
  );
};

export default InstanceSignIn;
